<template>
    <section class="min-h-screen overflow-hidden flex items-center justify-center relative">
        <!-- dust effect -->
        <div ref="globeContainer" class="absolute inset-0 w-full h-full pointer-events-none z-0"></div>
        <!-- end dust effect -->
        <div class="container mx-auto px-4 text-center relative z-10">
            <div class="animate-fade-in">
                <img :src="designer" draggable="false" alt="Designer" class="mx-auto mb-6 w-[100px] md:w-[200px]" />
                <h1 class="text-5xl md:text-7xl font-bold mb-6">
                    <span class="text-gradient">Hi, i am Bahri</span>
                </h1>
                <p class="text-xl md:text-2xl text-secondary-600 dark:text-secondary-400 mb-8 max-w-2xl mx-auto">
                    Web Developer, Mobile Developer, Adms Developer
                </p>
                <div class="flex flex-col sm:flex-row gap-4 justify-center">
                    <router-link to="/projects" class="btn-primary">
                        <i class="fas fa-eye mr-2"></i>
                        Go to My Projects
                    </router-link>
                    <a href="https://wa.me/6281808425604?text=Hello%20Bahriyanto%20Anang%2C%20I%20want%20to%20discuss%20about%20my%20project"
                        target="_blank" class="btn-secondary">
                        <i class="fas fa-envelope mr-2"></i>
                        Contact Me
                    </a>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from 'vue'
import * as THREE from 'three'
import designer from '@/assets/designer.svg'

const globeContainer = ref(null)
let renderer, scene, camera, particles, animationId
let mouse = { x: 0, y: 0 }
let particlePositions = [] // Menyimpan posisi awal partikel
let time = 0 // Untuk animasi
let cursor3D = { x: 0, y: 0, z: 0 }
let isCursorInWindow = true;
let dustStates = [];

onMounted(() => {
    // Nonaktifkan scroll pada body
    document.body.style.overflow = 'hidden';

    // Nonaktifkan klik kanan
    // window.addEventListener('contextmenu', preventRightClick)

    // Init scene
    scene = new THREE.Scene()
    camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000)
    camera.position.z = 6

    renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true })
    renderer.setClearColor(0x000000, 0)
    renderer.setSize(window.innerWidth, window.innerHeight)
    globeContainer.value.appendChild(renderer.domElement)

    // Light
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7)
    scene.add(ambientLight)
    const pointLight = new THREE.PointLight(0xffffff, 1)
    pointLight.position.set(5, 5, 5)
    scene.add(pointLight)

    // Particles (serpihan debu)
    const particleCount = 4000
    const particleGeometry = new THREE.BufferGeometry()
    const positions = []
    particlePositions = []
    for (let i = 0; i < particleCount; i++) {
        const radius = 1.5 + Math.random() * 3.5
        const phi = Math.acos(2 * Math.random() - 1)
        const theta = 2 * Math.PI * Math.random()
        const x = radius * Math.sin(phi) * Math.cos(theta)
        const y = radius * Math.sin(phi) * Math.sin(theta)
        const z = radius * Math.cos(phi)
        particlePositions.push({ x, y, z })
        positions.push(x, y, z)
    }
    particleGeometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3))
    const particleMaterial = new THREE.PointsMaterial({ color: 0x60a5fa, size: 0.01, transparent: true, opacity: 0.55 })
    particles = new THREE.Points(particleGeometry, particleMaterial)
    scene.add(particles)

    // Mouse interaction
    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('resize', onResize)
    window.addEventListener('mouseenter', onMouseEnter)
    window.addEventListener('mouseleave', onMouseLeave)

    initDustStates(particleCount);
    animate()
})

onBeforeUnmount(() => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('resize', onResize)
    window.removeEventListener('mouseenter', onMouseEnter)
    window.removeEventListener('mouseleave', onMouseLeave)
    cancelAnimationFrame(animationId)
    renderer?.dispose()
    // Aktifkan kembali scroll pada body
    document.body.style.overflow = '';
    window.removeEventListener('contextmenu', preventRightClick)
})

function onMouseMove(e) {
    mouse.x = (e.clientX / window.innerWidth) * 2 - 1;
    mouse.y = -(e.clientY / window.innerHeight) * 2 + 1;
    // Proyeksikan posisi mouse ke bidang tetap di depan kamera (misal z = 0)
    const vector = new THREE.Vector3(mouse.x, mouse.y, 0);
    vector.unproject(camera);
    cursor3D.x = vector.x;
    cursor3D.y = vector.y;
    cursor3D.z = vector.z;
}

function onMouseEnter() {
    isCursorInWindow = true
}

function onMouseLeave() {
    isCursorInWindow = false
}

function onResize() {
    camera.aspect = window.innerWidth / window.innerHeight
    camera.updateProjectionMatrix()
    renderer.setSize(window.innerWidth, window.innerHeight)
}

function initDustStates(count) {
    dustStates = [];
    for (let i = 0; i < count; i++) {
        const px = particlePositions[i].x;
        const py = particlePositions[i].y;
        const pz = particlePositions[i].z;
        dustStates.push({
            pos: { x: px, y: py, z: pz },
            vel: { x: 0, y: 0, z: 0 },
            home: { x: px, y: py, z: pz }
        });
    }
}

function animate() {
    time += 0.01
    // Rotasi partikel debu
    particles.rotation.y += 0.002
    particles.rotation.x += 0.0005
    // Simulasi gravitasi Newton pada partikel debu
    const positions = particles.geometry.attributes.position.array
    for (let i = 0; i < dustStates.length; i++) {
        let target;
        if (isCursorInWindow) {
            target = cursor3D;
        } else {
            target = dustStates[i].home;
        }
        // Gaya gravitasi Newton: F = G / r^2
        const dx = target.x - dustStates[i].pos.x;
        const dy = target.y - dustStates[i].pos.y;
        const dz = target.z - dustStates[i].pos.z;
        const distSq = dx * dx + dy * dy + dz * dz + 0.01;
        const dist = Math.sqrt(distSq);
        const G = 0.08;
        const force = G / distSq;
        const fx = (dx / dist) * force;
        const fy = (dy / dist) * force;
        const fz = (dz / dist) * force;
        dustStates[i].vel.x += fx;
        dustStates[i].vel.y += fy;
        dustStates[i].vel.z += fz;
        dustStates[i].vel.x *= 0.92;
        dustStates[i].vel.y *= 0.92;
        dustStates[i].vel.z *= 0.92;
        dustStates[i].pos.x += dustStates[i].vel.x;
        dustStates[i].pos.y += dustStates[i].vel.y;
        dustStates[i].pos.z += dustStates[i].vel.z;
        const index = i * 3;
        positions[index] = dustStates[i].pos.x;
        positions[index + 1] = dustStates[i].pos.y;
        positions[index + 2] = dustStates[i].pos.z;
    }
    particles.geometry.attributes.position.needsUpdate = true;
    renderer.render(scene, camera)
    animationId = requestAnimationFrame(animate)
}

function preventRightClick(e) {
    e.preventDefault();
}
</script>

<style scoped>
/* Globe sekarang berada di body, tidak perlu styling khusus */
</style>

<style>
#blackhole-cursor {
    box-shadow: 0 0 30px 10px #000a, 0 0 0 2px #222;
    transition: opacity 0.2s;
}
</style>